'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/AuthContext';
import { meetingsApi, Meeting } from '@/lib/api';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import styles from './UpcomingMeetings.module.css';
import { formatKathmanduDateTimeDisplay, isWithinNextHour } from '@/lib/timezone';

export function UpcomingMeetings() {
    const router = useRouter();
    const { user, token } = useAuth();
    const [meetings, setMeetings] = useState<Meeting[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchMeetings = useCallback(async () => {
        if (!user || !token) {
            setLoading(false);
            return;
        }
        try {
            const data = await meetingsApi.list(token, user.id);
            // Filter out instant meetings (title = "Instant Meeting")
            const scheduledMeetings = data.filter(meeting => meeting.title !== "Instant Meeting");
            setMeetings(scheduledMeetings);
        } catch (error) {
            console.error('Error fetching meetings:', error);
        } finally {
            setLoading(false);
        }
    }, [user, token]);

    useEffect(() => {
        fetchMeetings();
        // Poll for updates every 10 seconds
        const interval = setInterval(fetchMeetings, 10000);
        return () => clearInterval(interval);
    }, [fetchMeetings]);

    const handleStartMeeting = async (meeting: Meeting, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user || !token) return;

        try {
            await meetingsApi.start(token, meeting.id, user.id);
            toast.success('Meeting started!');
            await fetchMeetings();
        } catch (error) {
            toast.error('Failed to start meeting');
        }
    };

    const handleJoinMeeting = (meeting: Meeting) => {
        if (!meeting.isActive && meeting.createdBy === user?.id) {
            Swal.fire({
                title: 'Meeting Not Started',
                text: 'Would you like to start this meeting now?',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Start & Join',
                cancelButtonText: 'Cancel'
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        if (!user || !token) return;
                        await meetingsApi.start(token, meeting.id, user.id);
                        router.push(`/rooms/${meeting.roomName}`);
                    } catch (error) {
                        toast.error('Failed to start meeting');
                    }
                }
            });
            return;
        }

        if (!meeting.isActive) {
            toast.error('This meeting has not started yet.');
            return;
        }

        router.push(`/rooms/${meeting.roomName}`);
    };

    const handleDeleteMeeting = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        Swal.fire({
            title: 'Delete meeting?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#333',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (!user || !token) return;
                    await meetingsApi.delete(token, id, user.id);
                    await fetchMeetings();
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Meeting has been removed.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                } catch (error) {
                    console.error('Error deleting:', error);
                }
            }
        });
    };

    const handleCopyLink = (meeting: Meeting, e: React.MouseEvent) => {
        e.stopPropagation();
        const url = `${window.location.origin}/rooms/${meeting.roomName}`;
        navigator.clipboard.writeText(url).then(() => {
            toast.success('Meeting link copied!');
        }).catch(() => {
            toast.error('Failed to copy link');
        });
    };

    const formatMeetingDateTime = (dateStr: string) => {
        return formatKathmanduDateTimeDisplay(dateStr);
    };

    const isMeetingSoon = (dateStr: string) => {
        return isWithinNextHour(dateStr);
    };

    if (loading) {
        return (
            <div className={styles.loadingState}>
                <div className={styles.loadingSpinner}></div>
                <span>Loading meetings...</span>
            </div>
        );
    }

    if (meetings.length === 0) {
        return (
            <div className={styles.emptyState}>
                <span className={styles.emptyIcon}>📅</span>
                <p>No upcoming meetings</p>
                <span className={styles.emptyHint}>Schedule a meeting to get started</span>
            </div>
        );
    }

    return (
        <div className={styles.meetingsList}>
            {meetings.map((meeting) => (
                <div
                    key={meeting.id}
                    className={`${styles.meetingCard} ${meeting.isActive ? styles.active : ''}`}
                    onClick={() => handleJoinMeeting(meeting)}
                >
                    <div className={styles.meetingInfo}>
                        <div className={styles.meetingHeader}>
                            <h4 className={styles.meetingTitle}>{meeting.title}</h4>
                            {meeting.isActive && (
                                <span className={styles.liveBadge}>
                                    <span className={styles.liveDot}></span>
                                    Live
                                </span>
                            )}
                            {!meeting.isActive && isMeetingSoon(meeting.scheduledAt) && (
                                <span className={styles.soonBadge}>Starting Soon</span>
                            )}
                        </div>
                        <p className={styles.meetingDateTime}>
                            {formatMeetingDateTime(meeting.scheduledAt)}
                        </p>
                        {meeting.description && (
                            <p className={styles.meetingDescription}>{meeting.description}</p>
                        )}
                    </div>
                    <div className={styles.meetingActions}>
                        {!meeting.isActive && meeting.createdBy === user?.id && (
                            <button
                                className={styles.startBtn}
                                onClick={(e) => handleStartMeeting(meeting, e)}
                            >
                                Start
                            </button>
                        )}
                        <button
                            className={styles.copyLinkBtn}
                            onClick={(e) => handleCopyLink(meeting, e)}
                            title="Copy Meeting Link"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                            </svg>
                        </button>
                        <button
                            className={styles.deleteBtn}
                            onClick={(e) => handleDeleteMeeting(meeting.id, e)}
                            title="Delete Meeting"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                            </svg>
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}
